import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getDocumentRules, getDocumentRuleStaff, putProfileDocumentRule, putPersonDocumentRule, resetPersonDocumentRule,
} from '../api/admin';
import { useLanguage } from '../i18n/LanguageContext';

// Permissions & History Development Plan (13.10.2026), Phase 4 settings
// page: "Case Control › Settings › History & Document Rights." Defaults for
// internal/external accounts, plus individual per-person overrides. "The
// default must remain: the assignee may view and download both of their
// own documents as soon as the relevant document is available. Any
// restriction must require an explicit Owner setting" — every field here
// starts at that default; nothing changes for anyone until this form saves.
const HISTORY_VISIBLE_OPTIONS = [
  { value: 'own', label: 'Own processed assignments' },
  { value: 'none', label: 'None' },
  { value: 'released', label: 'Explicitly released assignments' },
];
const VIEW_ORDER_DOCUMENT_OPTIONS = [
  { value: 'own_accepted', label: 'For own accepted assignments' },
  { value: 'no', label: 'No' },
  { value: 'after_completion', label: 'Only after completion' },
];
const VIEW_DELIVERY_NOTE_OPTIONS = [
  { value: 'after_success', label: 'Only after successful acceptance' },
  { value: 'no', label: 'No' },
  { value: 'after_decision', label: 'After documented acceptance decision' },
];
const OTHER_COMPLETED_OPTIONS = [
  { value: 'released_individually', label: 'Released individually only' },
  { value: 'not_allowed', label: 'Not allowed' },
];
const DOWNLOAD_OPTIONS = [
  { value: true, label: 'Allowed' },
  { value: false, label: 'Not allowed' },
];

function RuleForm({ rule, onChange, disabled }) {
  return (
    <div style={s.grid}>
      <div style={s.field}>
        <label style={s.label}>History visible</label>
        <select style={s.select} value={rule.history_visible} disabled={disabled} onChange={(e) => onChange({ ...rule, history_visible: e.target.value })}>
          {HISTORY_VISIBLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={s.field}>
        <label style={s.label}>Other people's completed assignments</label>
        <select style={s.select} value={rule.other_completed_assignments} disabled={disabled} onChange={(e) => onChange({ ...rule, other_completed_assignments: e.target.value })}>
          {OTHER_COMPLETED_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={s.field}>
        <label style={s.label}>View Order Document</label>
        <select style={s.select} value={rule.view_order_document} disabled={disabled} onChange={(e) => onChange({ ...rule, view_order_document: e.target.value })}>
          {VIEW_ORDER_DOCUMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={s.field}>
        <label style={s.label}>Download Order Document</label>
        <select style={s.select} value={String(rule.download_order_document)} disabled={disabled} onChange={(e) => onChange({ ...rule, download_order_document: e.target.value === 'true' })}>
          {DOWNLOAD_OPTIONS.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
        </select>
      </div>
      <div style={s.field}>
        <label style={s.label}>View Delivery Note</label>
        <select style={s.select} value={rule.view_delivery_note} disabled={disabled} onChange={(e) => onChange({ ...rule, view_delivery_note: e.target.value })}>
          {VIEW_DELIVERY_NOTE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={s.field}>
        <label style={s.label}>Download Delivery Note</label>
        <select style={s.select} value={String(rule.download_delivery_note)} disabled={disabled} onChange={(e) => onChange({ ...rule, download_delivery_note: e.target.value === 'true' })}>
          {DOWNLOAD_OPTIONS.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}

const CaseControlSettings = () => {
  const { t } = useLanguage();
  const [defaults, setDefaults] = useState(null);
  const [profileRules, setProfileRules] = useState({ internal: null, external: null });
  const [staff, setStaff] = useState([]);
  const [personRules, setPersonRules] = useState({}); // admin_id -> rule row (or undefined = using profile default)
  const [savingProfile, setSavingProfile] = useState(null); // 'internal' | 'external' | null
  const [overrideFor, setOverrideFor] = useState(null); // admin_id of the person currently being edited
  const [overrideDraft, setOverrideDraft] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([getDocumentRules(), getDocumentRuleStaff()]).then(([rulesRes, staffRes]) => {
      const defs = rulesRes.data?.defaults;
      setDefaults(defs);
      const rows = rulesRes.data?.rules || [];
      const byProfile = { internal: null, external: null };
      const byPerson = {};
      for (const r of rows) {
        if (r.account_profile) byProfile[r.account_profile] = r;
        else if (r.admin_id) byPerson[r.admin_id] = r;
      }
      setProfileRules(byProfile);
      setPersonRules(byPerson);
      setStaff(staffRes.data?.staff || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const ruleFor = (profile) => profileRules[profile] || defaults;

  const saveProfile = async (profile, rule) => {
    setSavingProfile(profile);
    try {
      await putProfileDocumentRule(profile, rule);
      toast.success(`${profile === 'internal' ? 'Internal' : 'External'} defaults saved`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save defaults');
    } finally {
      setSavingProfile(null);
    }
  };

  const openOverride = (person) => {
    setOverrideFor(person.admin_id);
    setOverrideDraft(personRules[person.admin_id] || ruleFor(person.account_profile) || defaults);
  };

  const saveOverride = async () => {
    if (!overrideFor) return;
    setSavingOverride(true);
    try {
      await putPersonDocumentRule(overrideFor, overrideDraft);
      toast.success('Override saved');
      setOverrideFor(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save override');
    } finally {
      setSavingOverride(false);
    }
  };

  const clearOverride = async (adminId) => {
    if (!window.confirm('Reset this person back to their account-profile default?')) return;
    try {
      await resetPersonDocumentRule(adminId);
      toast.success('Reset to default');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not reset override');
    }
  };

  if (loading || !defaults) return <div style={s.page}><p>Loading…</p></div>;

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>{t('Case Management')} › {t('History & Document Rights')}</div>
      <h1 style={s.h1}>{t('History & Document Rights')}</h1>
      <p style={s.hint}>
        What matters is not whether someone is internal or external, but whether the person works on the
        assignment. The basic rule stays the default everywhere below: the assignee may view and download
        both of their own documents as soon as each document is available. Change a field here only to set
        an explicit exception.
      </p>

      <h2 style={s.h2}>Internal — default</h2>
      <RuleForm rule={ruleFor('internal')} onChange={(r) => setProfileRules((p) => ({ ...p, internal: r }))} disabled={savingProfile === 'internal'} />
      <button style={s.btnGreen} disabled={savingProfile === 'internal'} onClick={() => saveProfile('internal', ruleFor('internal'))}>
        {savingProfile === 'internal' ? 'Saving…' : 'Save internal defaults'}
      </button>

      <h2 style={s.h2}>External — default</h2>
      <RuleForm rule={ruleFor('external')} onChange={(r) => setProfileRules((p) => ({ ...p, external: r }))} disabled={savingProfile === 'external'} />
      <button style={s.btnGreen} disabled={savingProfile === 'external'} onClick={() => saveProfile('external', ruleFor('external'))}>
        {savingProfile === 'external' ? 'Saving…' : 'Save external defaults'}
      </button>

      <h2 style={s.h2}>Individual overrides</h2>
      <p style={s.hint}>Only people with an override below differ from their account-profile default.</p>
      <table style={s.table}>
        <thead><tr>
          <th style={s.th}>Name</th><th style={s.th}>Profile</th><th style={s.th}>Override</th><th style={s.th}>Actions</th>
        </tr></thead>
        <tbody>
          {staff.map((p) => (
            <tr key={p.admin_id}>
              <td style={s.td}>{p.full_name || p.email}{!p.is_active && <span style={s.mutedSmall}> (inactive)</span>}</td>
              <td style={s.td}>{p.account_profile === 'internal' ? 'Internal' : 'External'}</td>
              <td style={s.td}>{personRules[p.admin_id] ? <span style={s.badge}>Custom</span> : <span style={s.mutedSmall}>Using {p.account_profile} default</span>}</td>
              <td style={s.td}>
                <button style={s.btnGray} onClick={() => openOverride(p)}>Edit override</button>
                {personRules[p.admin_id] && <button style={s.btnGray} onClick={() => clearOverride(p.admin_id)}>Reset</button>}
              </td>
            </tr>
          ))}
          {staff.length === 0 && <tr><td style={s.td} colSpan={4}>No staff accounts yet.</td></tr>}
        </tbody>
      </table>

      {overrideFor && overrideDraft && (
        <div style={s.modalBackdrop} onClick={() => setOverrideFor(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Override for {staff.find((p) => p.admin_id === overrideFor)?.full_name || 'this person'}</h3>
            <RuleForm rule={overrideDraft} onChange={setOverrideDraft} disabled={savingOverride} />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button style={s.btnGreen} disabled={savingOverride} onClick={saveOverride}>{savingOverride ? 'Saving…' : 'Save override'}</button>
              <button style={s.btnGray} onClick={() => setOverrideFor(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1000 },
  breadcrumb: { fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  h2: { fontSize: 16, fontWeight: 800, marginTop: 28, marginBottom: 10, color: '#374151' },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 14, maxWidth: 760 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 700, color: '#374151' },
  select: { padding: '7px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 },
  btnGreen: { marginTop: 10, padding: '8px 16px', borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: 13, marginTop: 8 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  mutedSmall: { color: '#9ca3af', fontSize: 11.5 },
  badge: { padding: '2px 8px', borderRadius: 999, background: '#fef9c3', color: '#92400e', fontSize: 11, fontWeight: 700 },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 },
  modal: { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto' },
};

export default CaseControlSettings;
